import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {jobKeys} from 'src/jobs/query-keys'
import {projectKeys} from 'src/projects/query-keys'
import {quoteKeys} from 'src/quotes/query-keys'

import {
    createCustomer,
    createCustomerContact,
    deleteCustomerContact,
    getCustomer,
    listCustomers,
    updateCustomer,
    updateCustomerContact,
    updateCustomerPaymentTerms,
} from './api'
import {customerKeys} from './query-keys'
import type {
    ContactWriteInput,
    CreateCustomerInput,
    CustomerDetail,
    CustomerListFilters,
    UpdateCustomerInput,
} from './schemas'

export function useCustomers(filters: CustomerListFilters = {}, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: customerKeys.list(filters),
        queryFn: () => listCustomers(filters),
        enabled: options?.enabled ?? true,
    })
}

export function useCustomer(customerId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: customerKeys.detail(customerId),
        queryFn: () => getCustomer(customerId),
        enabled: options?.enabled ?? Boolean(customerId),
    })
}

export function useCreateCustomer() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateCustomerInput) => createCustomer(input),
        onSuccess: (customer) => {
            void queryClient.invalidateQueries({queryKey: customerKeys.lists()})
            queryClient.setQueryData(customerKeys.detail(customer.id), customer)
        },
    })
}

export function useUpdateCustomer(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: UpdateCustomerInput) => updateCustomer(customerId, input),
        onSuccess: (customer) => {
            void queryClient.invalidateQueries({queryKey: customerKeys.lists()})
            queryClient.setQueryData(customerKeys.detail(customerId), customer)
        },
    })
}

export function useUpdateCustomerPaymentTerms(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (paymentTerms: string | null) =>
            updateCustomerPaymentTerms(customerId, paymentTerms),
        onSuccess: (customer) => {
            queryClient.setQueryData(customerKeys.detail(customerId), customer)
        },
    })
}

export function useCreateCustomerContact(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: ContactWriteInput) => createCustomerContact(customerId, input),
        onSuccess: (createdContact) => {
            queryClient.setQueryData<CustomerDetail>(customerKeys.detail(customerId), (current) =>
                current ? {...current, contacts: [...current.contacts, createdContact]} : current,
            )
            void queryClient.invalidateQueries({queryKey: customerKeys.lists()})
        },
    })
}

export function useUpdateCustomerContact(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({contactId, input}: {contactId: string; input: ContactWriteInput}) =>
            updateCustomerContact(customerId, contactId, input),
        onSuccess: (updatedContact) => {
            queryClient.setQueryData<CustomerDetail>(customerKeys.detail(customerId), (current) =>
                current
                    ? {
                          ...current,
                          contacts: current.contacts.map((contact) =>
                              contact.id === updatedContact.id ? updatedContact : contact,
                          ),
                      }
                    : current,
            )
            void queryClient.invalidateQueries({queryKey: projectKeys.details()})
            void queryClient.invalidateQueries({queryKey: jobKeys.all})
            void queryClient.invalidateQueries({queryKey: quoteKeys.all})
        },
    })
}

export function useDeleteCustomerContact(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (contactId: string) => deleteCustomerContact(customerId, contactId),
        onSuccess: (_result, contactId) => {
            queryClient.setQueryData<CustomerDetail>(customerKeys.detail(customerId), (current) =>
                current
                    ? {
                          ...current,
                          contacts: current.contacts.filter((contact) => contact.id !== contactId),
                      }
                    : current,
            )
            void queryClient.invalidateQueries({queryKey: customerKeys.lists()})
        },
    })
}
